// Display helpers for admin user/device status & roles. Pure presentation; values
// observed live (FACTS PACK §3) plus the obvious lifecycle states the action bar drives.

import type { AdminUser } from './usersApi';

// ---- user status ----

export type UserStatusTone = 'good' | 'warn' | 'danger' | 'neutral';

const USER_STATUS_LABEL: Record<string, string> = {
  active: '使用中',
  pending_verification: '待驗證',
  suspended: '已停權',
  deactivated: '已停用',
  banned: '已封禁',
  deleting: '刪除中',
  deleted: '已刪除',
};

const USER_STATUS_TONE: Record<string, UserStatusTone> = {
  active: 'good',
  pending_verification: 'warn',
  suspended: 'warn',
  deactivated: 'warn',
  banned: 'danger',
  deleting: 'warn',
  deleted: 'danger',
};

export function userStatusLabel(status: string | undefined): string {
  if (!status) return '—';
  return USER_STATUS_LABEL[status] ?? status;
}

export function userStatusTone(status: string | undefined): UserStatusTone {
  if (!status) return 'neutral';
  return USER_STATUS_TONE[status] ?? 'neutral';
}

/** Default browse excludes deletion lifecycle rows; an explicit status filter
 * restores them for compliance/support inspection. */
export function userBrowseStatusMatch(selectedStatus: string, status: string | undefined): boolean {
  if (selectedStatus) return status === selectedStatus;
  return status !== 'deleting' && status !== 'deleted';
}

// ---- roles ----

// 全站角色譯名單一事實來源(dashboard 等頁一律 import 此 roleLabel(),勿各自另建 map)。
const ROLE_LABEL: Record<string, string> = {
  user: '一般使用者',
  developer: '開發者',
  admin: '系統管理員',
  'security-officer': '資安專員',
  'platform-owner': '平台擁有者',
  'cs-readonly': '客服唯讀',
  'helpdesk-agent': 'Helpdesk 專員',
  'helpdesk-lead': 'Helpdesk Lead',
  owner: '擁有者',
  shared: '共享',
};

// ---- identity classes (S2 身分分頁) ----
// The users table carries three identity classes in one `role` column. /users owns
// the END-USER classes only (app users + developers); maintainer accounts are
// governed on /team (S9) and never appear in either tab.
// Sets here must stay aligned with the common/03 users.role enum (common!67).

export type IdentityClass = 'app' | 'developer';

export const MAINTAINER_ROLES = ['admin', 'platform-owner', 'security-officer', 'cs-readonly', 'helpdesk-agent', 'helpdesk-lead'];
export const END_USER_ROLES = ['user', 'developer'];
export const DEVELOPER_MANAGEMENT_FAMILY = 'fam-devmgmt';

/** Server-side role= set for an identity tab. Every browse request is role-scoped. */
export function identityRoles(c: IdentityClass): string[] {
  if (c === 'app') return ['user'];
  return ['developer'];
}

/** Client-side membership test for an identity tab (belt-and-braces). */
export function identityMatch(c: IdentityClass, role: string | undefined, familyId?: string): boolean {
  const r = role ?? 'user';
  // Developer Portal applicants remain role=user until approval. family_id is
  // therefore the required second axis separating them from real App users.
  if (c === 'app') return r === 'user' && familyId !== DEVELOPER_MANAGEMENT_FAMILY;
  return r === 'developer';
}

/** Visual tone class for the role pill (users.css .u-role-*). */
export function roleClass(role: string | undefined): string {
  if (!role) return '';
  if (MAINTAINER_ROLES.includes(role)) return 'u-role-maintainer';
  if (role === 'developer') return 'u-role-developer';
  return '';
}

export function roleLabel(role: string | undefined): string {
  if (!role) return '—';
  return ROLE_LABEL[role] ?? role;
}

// ---- device status ----

const DEVICE_STATUS_LABEL: Record<string, string> = {
  active: '正常',
  disabled: '已停用',
  lost: '已掛失',
  reported_lost: '已掛失',
};

export function deviceStatusLabel(status: string | undefined): string {
  if (!status) return '—';
  return DEVICE_STATUS_LABEL[status] ?? status;
}

export function deviceOnlineLabel(state: string | undefined): string {
  if (state === 'online') return '在線';
  if (state === 'offline') return '離線';
  return state ?? '—';
}

export function deviceName(d: { display_name?: string; device_id: string }): string {
  return d.display_name || d.device_id;
}

// ---- subscription ----

const SUB_STATUS_LABEL: Record<string, string> = {
  active: '生效中',
  expired: '已到期',
  cancelled: '已取消',
  none: '無',
};

export function subStatusLabel(status: string | undefined): string {
  if (!status) return '—';
  return SUB_STATUS_LABEL[status] ?? status;
}

// ---- event types (best-effort; unknown types fall through to raw) ----

const EVENT_TYPE_LABEL: Record<string, string> = {
  object_person: '人形偵測',
  object_vehicle: '車輛偵測',
  object_pet: '寵物偵測',
  motion: '移動偵測',
  sound: '聲音偵測',
  ring: '門鈴',
  'account.export_request': '帳號資料匯出',
  'device.share': '裝置分享',
};

export function eventTypeLabel(t: string | undefined): string {
  if (!t) return '—';
  return EVENT_TYPE_LABEL[t] ?? t;
}

// ---- lifecycle action gating ----
// Which actions apply given the current user status. Server enforces too (requireAdmin),
// but gating the UI avoids obvious no-ops. (FACTS PACK: status values active /
// pending_verification observed; suspended/banned inferred from action names.)

export interface ActionGate {
  suspend: boolean;
  unsuspend: boolean;
  ban: boolean;
  forceLogout: boolean;
  impersonate: boolean;
}

export function actionGate(user: AdminUser | null): ActionGate {
  const status = user?.status;
  const banned = status === 'banned';
  const suspended = status === 'suspended';
  return {
    // suspend: anything not already suspended/banned
    suspend: !suspended && !banned,
    // unsuspend: only when suspended
    unsuspend: suspended,
    // ban: anything not already banned
    ban: !banned,
    // force-logout: any live (non-banned) account
    forceLogout: !banned,
    // impersonate: only an active account is worth impersonating; never a banned one
    impersonate: status === 'active',
  };
}
